import { useContextNonNull } from 'components/use-context-non-null';
import type { TrafficEvent, TrafficProjectCampaignPageQuery } from 'generated/graphql';
import { createContext, type FC, type ReactNode, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

export type TPCampaignContextValue = {
  projectEvents: TrafficEvent[] | undefined;
  setProjectEvents: (projectEvents?: TrafficEvent[]) => void;
  error?: Error;
  setError: (error?: Error) => void;
  loading?: boolean;
  setLoading: (loading: boolean) => void;
  isNew?: boolean;
  campaignType?: string;
  campaignId?: string;
  trafficProjectId: string;
  dirty: boolean;
  setDirty: (dirty: boolean) => void;
  setDefaultValueEdited: (defaultValueEdited: boolean) => void;
  defaultValueEdited: boolean;
};
export const TPCampaignContext = createContext<TPCampaignContextValue | null>(null);

export const TPCampaignContextProvider: FC<{
  isNew?: boolean;
  trafficProjectName?: string;
  children?: ReactNode;
}> = ({ children, isNew }) => {
  const { campaignType, campaignId, trafficProjectId } = useParams<{
    campaignType?: string;
    campaignId?: string;
    trafficProjectId: string;
  }>();
  const [loading, setLoading] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState<Error>();
  const [defaultValueEdited, setDefaultValueEdited] = useState(false);
  const [projectEvents, setProjectEvents] = useState<TrafficEvent[] | undefined>(undefined);

  const value = useMemo(
    () => ({
      campaignId,
      campaignType,
      defaultValueEdited,
      dirty,
      error,
      isNew,
      loading,
      projectEvents,
      setDefaultValueEdited,
      setDirty,
      setError,
      setLoading,
      setProjectEvents,
      trafficProjectId,
    }),
    [
      projectEvents,
      error,
      loading,
      isNew,
      campaignType,
      campaignId,
      trafficProjectId,
      dirty,
      defaultValueEdited,
    ],
  );
  return <TPCampaignContext.Provider value={value}>{children}</TPCampaignContext.Provider>;
};

type UseTPCampaignContextProps = {
  loading?: boolean;
  error?: Error;
};

export function useTPCampaignContext(props: UseTPCampaignContextProps = {}): TPCampaignContextValue {
  const context = useContextNonNull(TPCampaignContext);
  const { setLoading, setError } = context;
  useEffect(
    () => setLoading(!!props.loading),
    [
      setLoading,
      props.loading,
    ],
  );
  useEffect(
    () => setError(props.error),
    [
      setError,
      props.error,
    ],
  );

  return context;
}

type ContextValueWithFlavors = TrafficProjectCampaignPageQuery['trafficCampaign'];
export const TPCampaignWithFlavorsContext = createContext<ContextValueWithFlavors | null>(null);

export const useTPCampaignWithFlavors = () => useContextNonNull(TPCampaignWithFlavorsContext);

export const useTPCampaignWithActiveFlavors = () => {
  const campaign = useTPCampaignWithFlavors();
  const activeFlavors = useMemo(
    () => campaign.flavors.filter((f) => f.active),
    [
      campaign,
    ],
  );

  return {
    ...campaign,
    flavors: activeFlavors,
  };
};
